import React, { useEffect, useRef, useState } from "react";
import { Bar } from "react-chartjs-2";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { Card, Box, Skeleton, useTheme } from "@mui/material";
import axios from "axios";
import { baseURL } from "../../utils/StaticVariables";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { authState, popupState } from "../../Recoil/RecoilState";
import DesBtn from "../Reusable/DesBtn";
import OrderFilter from "./OrderFilter";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels
);

const FireDetectionsByCameraChart = () => {
  const [dataList, setDataList] = useState([]);
  const [loading, setLoading] = useState(false);
  const { token } = useRecoilValue(authState);
  const setPopup = useSetRecoilState(popupState);
  const childRef = useRef(null);
  const theme = useTheme();

  const [filter, setFilter] = useState({ start_date: null, end_date: null });

  const fetchData = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(
        `${baseURL}analytics/fire/detections-by-camera`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: filter,
        }
      );
      setDataList(data.data || []);
    } catch (err) {
      console.error("Fire / Smoke detections error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [filter]);

  const labels = dataList.map((item) => item.camera_name);

  // 🔥 Fire colors
  const fireColors = dataList.map((item) => {
    if (item.fire_detection_rate < 0.1) return "rgba(230, 96, 81, 0.8)";
    if (item.fire_detection_rate < 0.3) return "rgba(219, 43, 23, 0.82)";
    return "rgba(223, 22, 0, 0.8)";
  });

  // 💨 Smoke colors
  const smokeColors = dataList.map((item) => {
    if (item.smoke_detection_rate < 0.1) return "rgba(187, 187, 187, 0.8)";
    if (item.smoke_detection_rate < 0.3) return "rgba(173, 173, 173, 1)";
    return "rgba(77, 76, 76, 1)";
  });

  const data = {
    labels,
    datasets: [
      {
        label: "Fire Detections",
        data: dataList.map((item) =>
          item.fire_detections > 0 ? item.fire_detections : null
        ),
        backgroundColor: fireColors,
      },
      {
        label: "Smoke Detections",
        data: dataList.map((item) =>
          item.smoke_detections > 0 ? item.smoke_detections : null
        ),
        backgroundColor: smokeColors,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        labels: { color: theme.palette.text.primary },
      },
      tooltip: {
        callbacks: {
          label: function (ctx) {
            const item = dataList[ctx.dataIndex];

            if (ctx.dataset.label === "Fire Detections") {
              if (!item.fire_detections) return null;
              return [
                `🔥 Fire: ${item.fire_detections}`,
                `Rate: ${(item.fire_detection_rate * 100).toFixed(2)}%`,
                `Days: ${item.days_with_fire}`,
                `First: ${new Date(
                  item.first_fire_detection
                ).toLocaleString()}`,
                `Last: ${new Date(
                  item.last_fire_detection
                ).toLocaleString()}`,
              ];
            }

            if (!item.smoke_detections) return null;
            return [
              `💨 Smoke: ${item.smoke_detections}`,
              `Rate: ${(item.smoke_detection_rate * 100).toFixed(2)}%`,
              `Days: ${item.days_with_smoke}`,
              `First: ${new Date(
                item.first_smoke_detection
              ).toLocaleString()}`,
              `Last: ${new Date(
                item.last_smoke_detection
              ).toLocaleString()}`,
            ];
          },
        },
      },
      title: {
        display: true,
        text: "Fire & Smoke Detections by Camera",
        color: theme.palette.text.primary,
      },
      datalabels: {
        display: (ctx) => ctx.dataset.data[ctx.dataIndex] !== null,
        color: theme.palette.text.primary,
        font: { weight: "bold" },
        anchor: "center",
        align: "center",
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: { color: theme.palette.text.primary },
        grid: { color: theme.palette.divider },
        title: {
          display: true,
          text: "Detection Count",
          color: theme.palette.text.primary,
        },
      },
      x: {
        ticks: { color: theme.palette.text.primary },
        grid: { color: theme.palette.divider },
        title: {
          display: true,
          text: "Camera Name",
          color: theme.palette.text.primary,
        },
      },
    },
  };

  const openFilter = () => {
    setPopup({
      isOpen: true,
      title: "Filter by Date",
      content: (
        <OrderFilter
          ref={childRef}
          filter={filter}
          changeFilterHandle={setFilter}
        />
      ),
      sendReq: () => childRef.current?.submit(),
    });
  };

  return (
    <Card sx={{ p: 2 }}>
      <DesBtn text="Filter" handle={openFilter}>
        <FilterAltOutlinedIcon />
      </DesBtn>

      {loading ? (
        <Skeleton variant="rectangular" height="500px" />
      ) : (
        <Box sx={{ height: "500px" }}>
          <Bar data={data} options={options} plugins={[ChartDataLabels]} />
        </Box>
      )}
    </Card>
  );
};

export default FireDetectionsByCameraChart;
